'use client';

import { useState, useEffect } from 'react';
import { useTranslations } from 'next-intl';

export default function BasicCalculator() {
  const t = useTranslations("tools.basicCalculator");
  const [screenSize, setScreenSize] = useState('mobile');

  // Calculator state
  const [currentInput, setCurrentInput] = useState('0');
  const [previousInput, setPreviousInput] = useState('');
  const [operator, setOperator] = useState('');
  const [shouldResetInput, setShouldResetInput] = useState(false);
  const [memory, setMemory] = useState(0);
  const [lastAnswer, setLastAnswer] = useState(0);

  useEffect(() => {
    const updateScreenSize = () => {
      const width = window.innerWidth;
      if (width >= 1024) {
        setScreenSize('desktop');
      } else if (width >= 768) {
        setScreenSize('tablet');
      } else {
        setScreenSize('mobile');
      }
    };

    updateScreenSize();
    window.addEventListener('resize', updateScreenSize);
    return () => window.removeEventListener('resize', updateScreenSize);
  }, []);

  useEffect(() => {
    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key >= '0' && e.key <= '9' || e.key === '.') {
        appendNumber(e.key);
      } else if (e.key === '+' || e.key === '-' || e.key === '*' || e.key === '/') {
        setOperatorHandler(e.key);
      } else if (e.key === 'Enter' || e.key === '=') {
        e.preventDefault();
        calculate();
      } else if (e.key === 'Escape' || e.key === 'c' || e.key === 'C') {
        clear();
      } else if (e.key === 'Backspace') {
        backspace();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [currentInput, previousInput, operator, shouldResetInput]);

  const getResponsiveStyle = (baseStyle: any, mobileOverrides?: any, tabletOverrides?: any, desktopOverrides?: any) => {
    let style = { ...baseStyle };

    if (screenSize === 'mobile' && mobileOverrides) {
      style = { ...style, ...mobileOverrides };
    } else if (screenSize === 'tablet' && tabletOverrides) {
      style = { ...style, ...tabletOverrides };
    } else if (screenSize === 'desktop' && desktopOverrides) {
      style = { ...style, ...desktopOverrides };
    }

    return style;
  };

  const appendNumber = (num: string) => {
    if (shouldResetInput) {
      setCurrentInput(num);
      setShouldResetInput(false);
    } else {
      if (currentInput === '0' && num !== '.') {
        setCurrentInput(num);
      } else if (num === '.' && currentInput.includes('.')) {
        return;
      } else {
        setCurrentInput(currentInput + num);
      }
    }
  };

  const setOperatorHandler = (op: string) => {
    if (operator && !shouldResetInput) {
      calculate();
    }
    setPreviousInput(currentInput);
    setOperator(op);
    setShouldResetInput(true);
  };

  const calculate = () => {
    if (!operator || !previousInput) return;

    const prev = parseFloat(previousInput);
    const current = parseFloat(currentInput);
    let result: number;

    switch(operator) {
      case '+':
        result = prev + current;
        break;
      case '-':
        result = prev - current;
        break;
      case '*':
        result = prev * current;
        break;
      case '/':
        if (current === 0) {
          setCurrentInput('Error');
          setOperator('');
          setPreviousInput('');
          setShouldResetInput(true);
          return;
        }
        result = prev / current;
        break;
      default:
        return;
    }

    result = Math.round(result * 1000000000000) / 1000000000000;
    setCurrentInput(result.toString());
    setLastAnswer(result);
    setOperator('');
    setPreviousInput('');
    setShouldResetInput(true);
  };

  const clear = () => {
    setCurrentInput('0');
    setPreviousInput('');
    setOperator('');
    setShouldResetInput(false);
  };

  const backspace = () => {
    if (currentInput.length > 1) {
      setCurrentInput(currentInput.slice(0, -1));
    } else {
      setCurrentInput('0');
    }
  };

  const changeSign = () => {
    if (currentInput !== '0') {
      if (currentInput.startsWith('-')) {
        setCurrentInput(currentInput.slice(1));
      } else {
        setCurrentInput('-' + currentInput);
      }
    }
  };

  const addExponent = () => {
    if (!currentInput.includes('e')) {
      setCurrentInput(currentInput + 'e');
    }
  };

  const random = () => {
    setCurrentInput(Math.random().toString());
    setShouldResetInput(true);
  };

  const memoryAdd = () => {
    setMemory(memory + (parseFloat(currentInput) || 0));
    setShouldResetInput(true);
  };

  const memorySubtract = () => {
    setMemory(memory - (parseFloat(currentInput) || 0));
    setShouldResetInput(true);
  };

  const memoryRecall = () => {
    setCurrentInput(memory.toString());
    setShouldResetInput(true);
  };

  const useAnswer = () => {
    setCurrentInput(lastAnswer.toString());
    setShouldResetInput(true);
  };

  const colors = {
    primary: '#ff5a70',
    primaryHover: '#f63b3b',
    secondary: '#ffc400',
    accent: '#ff6b95',
    textPrimary: '#2d3748',
    textSecondary: '#4a5568',
    glassBg: 'rgba(255, 255, 255, 0.6)',
    glassBorder: 'rgba(255, 200, 180, 0.2)',
  };

  const containerStyle = getResponsiveStyle(
    { maxWidth: '1200px', margin: '0 auto', position: 'relative' as const, zIndex: 1 },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '2rem' }
  );

  const headerStyle = getResponsiveStyle(
    { textAlign: 'center' as const, marginBottom: '2rem' },
    {},
    { marginBottom: '2.5rem' },
    { marginBottom: '3rem' }
  );

  const h1Style = getResponsiveStyle(
    {
      fontWeight: 700,
      color: colors.textPrimary,
      marginBottom: '1rem',
      lineHeight: 1.2,
      background: `linear-gradient(135deg, ${colors.primary} 0%, ${colors.secondary} 100%)`,
      WebkitBackgroundClip: 'text',
      WebkitTextFillColor: 'transparent',
      backgroundClip: 'text'
    },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '3rem' }
  );

  const summaryTextStyle = getResponsiveStyle(
    { color: colors.textSecondary, lineHeight: 1.6 },
    { fontSize: '1rem' },
    { fontSize: '1.125rem' },
    { fontSize: '1.25rem' }
  );

  const calculatorToolStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, rgba(240, 249, 255, 0.9) 0%, rgba(224, 242, 254, 0.9) 100%)',
      borderRadius: '1rem',
      marginBottom: '2rem',
    },
    { padding: '2rem 1rem' },
    { padding: '2.5rem 1.5rem' },
    { padding: '3rem 1.5rem' }
  );

  const toolContainerStyle = getResponsiveStyle(
    { margin: '0 auto', background: 'white', borderRadius: '1rem', boxShadow: '0 10px 30px rgba(0, 0, 0, 0.1)', overflow: 'hidden' as const },
    { maxWidth: '100%' },
    { maxWidth: '500px' },
    { maxWidth: '500px' }
  );

  const toolHeaderStyle = getResponsiveStyle(
    {
      background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)',
      color: 'white',
      textAlign: 'center' as const,
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2rem' }
  );

  const toolHeaderH2Style = getResponsiveStyle(
    { margin: 0, fontWeight: 700 },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '1.75rem' }
  );

  const calculatorDisplayStyle = getResponsiveStyle(
    {
      background: '#1e293b',
      display: 'flex',
      flexDirection: 'column' as const,
      justifyContent: 'flex-end',
      alignItems: 'flex-end',
      minHeight: '100px',
    },
    { padding: '1rem' },
    { padding: '1.5rem' },
    { padding: '1.5rem' }
  );

  const displayHistoryStyle = {
    color: '#94a3b8',
    fontSize: '0.875rem',
    minHeight: '1.25rem',
    marginBottom: '0.5rem',
    wordBreak: 'break-all' as const,
  };

  const displayOutputStyle = getResponsiveStyle(
    {
      color: 'white',
      fontWeight: 600,
      fontFamily: "'Courier New', monospace",
      wordBreak: 'break-all' as const,
    },
    { fontSize: '2rem' },
    { fontSize: '2.5rem' },
    { fontSize: '2.5rem' }
  );

  const calculatorButtonsStyle = getResponsiveStyle(
    {
      display: 'grid',
      gridTemplateColumns: 'repeat(5, 1fr)',
    },
    { padding: '1rem', gap: '0.5rem' },
    { padding: '1.5rem', gap: '0.75rem' },
    { padding: '1.5rem', gap: '0.75rem' }
  );

  const calcBtnBaseStyle = getResponsiveStyle(
    {
      fontWeight: 600,
      border: 'none',
      borderRadius: '0.5rem',
      cursor: 'pointer',
      transition: 'all 0.2s',
      background: '#f1f5f9',
      color: '#1e293b',
    },
    { padding: '1rem 0.25rem', fontSize: '1rem' },
    { padding: '1.25rem 0.5rem', fontSize: '1.125rem' },
    { padding: '1.25rem 0.5rem', fontSize: '1.125rem' }
  );

  const numberBtnStyle = { ...calcBtnBaseStyle, background: '#f1f5f9' };
  const operatorBtnStyle = { ...calcBtnBaseStyle, background: '#dbeafe', color: '#2563eb' };
  const functionBtnStyle = getResponsiveStyle(
    { ...calcBtnBaseStyle, background: '#fef3c7', color: '#f59e0b' },
    { fontSize: '0.75rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );
  const equalsBtnStyle = { ...calcBtnBaseStyle, background: 'linear-gradient(135deg, #2563eb 0%, #7c3aed 100%)', color: 'white' };
  const clearBtnStyle = { ...calcBtnBaseStyle, background: '#fee2e2', color: '#dc2626' };
  const memoryBtnStyle = getResponsiveStyle(
    { ...calcBtnBaseStyle, background: memory !== 0 ? '#7c3aed' : '#f3e8ff', color: memory !== 0 ? 'white' : '#7c3aed' },
    { fontSize: '0.75rem' },
    { fontSize: '0.875rem' },
    { fontSize: '0.875rem' }
  );

  const glassCardStyle = getResponsiveStyle(
    {
      background: 'rgba(255, 255, 255, 0.6)',
      backdropFilter: 'blur(20px) saturate(180%)',
      WebkitBackdropFilter: 'blur(20px) saturate(180%)',
      border: '1px solid rgba(255, 200, 180, 0.2)',
      borderRadius: '1rem',
      boxShadow: '0 8px 32px 0 rgba(255, 90, 112, 0.08)',
      marginBottom: '2rem',
    },
    { padding: '1.5rem' },
    { padding: '2rem' },
    { padding: '2.5rem' }
  );

  const h2Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '1rem' },
    { fontSize: '1.5rem' },
    { fontSize: '1.75rem' },
    { fontSize: '2rem' }
  );

  const h3Style = getResponsiveStyle(
    { fontWeight: 600, color: colors.textPrimary, marginBottom: '0.75rem', marginTop: '1.5rem' },
    { fontSize: '1.25rem' },
    { fontSize: '1.375rem' },
    { fontSize: '1.5rem' }
  );

  const paragraphStyle = getResponsiveStyle(
    { lineHeight: 1.7, color: colors.textSecondary, marginBottom: '1rem' },
    { fontSize: '1rem' },
    { fontSize: '1.0625rem' },
    { fontSize: '1.125rem' }
  );

  const listStyle = {
    ...paragraphStyle,
    paddingLeft: '1.5rem',
    marginBottom: '1rem',
  };

  const listItemStyle = {
    marginBottom: '0.75rem',
  };

  const featureGridStyle = getResponsiveStyle(
    { display: 'grid', gap: '1.5rem', marginTop: '1.5rem' },
    { gridTemplateColumns: '1fr' },
    { gridTemplateColumns: 'repeat(2, 1fr)' },
    { gridTemplateColumns: 'repeat(2, 1fr)' }
  );

  const featureCardStyle = {
    background: 'rgba(255, 255, 255, 0.5)',
    padding: '1.5rem',
    borderRadius: '0.75rem',
    border: '1px solid rgba(255, 200, 180, 0.15)',
  };

  const featureTitleStyle = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
  };

  const featureTextStyle = {
    fontSize: '0.9375rem',
    color: colors.textSecondary,
    lineHeight: 1.6,
  };

  const faqItemStyle = {
    marginBottom: '1.5rem',
  };

  const faqQuestionStyle = {
    fontSize: '1.125rem',
    fontWeight: 600,
    color: colors.textPrimary,
    marginBottom: '0.5rem',
  };

  const faqAnswerStyle = {
    fontSize: '1rem',
    color: colors.textSecondary,
    lineHeight: 1.6,
  };

  const referenceListStyle = {
    listStyle: 'none',
    padding: 0,
    margin: 0,
  };

  const referenceItemStyle = {
    marginBottom: '0.75rem',
    paddingLeft: '1.5rem',
    position: 'relative' as const,
  };

  const referenceLinkStyle = {
    color: colors.primary,
    textDecoration: 'none',
    fontSize: '0.9375rem',
    lineHeight: 1.6,
    transition: 'color 0.2s',
  };

  return (
    <div style={containerStyle}>
      <header style={headerStyle}>
        <h1 style={h1Style}>{t("title")}</h1>
        <p style={summaryTextStyle}>{t("subtitle")}</p>
      </header>

      <section style={calculatorToolStyle}>
        <div style={toolContainerStyle}>
          <div style={toolHeaderStyle}>
            <h2 style={toolHeaderH2Style}>🧮 {t("toolTitle")}</h2>
          </div>

          <div style={calculatorDisplayStyle}>
            <div style={displayHistoryStyle}>
              {previousInput && operator ? `${previousInput} ${operator === '*' ? '×' : operator === '/' ? '÷' : operator === '-' ? '−' : operator}` : ''}
            </div>
            <div style={displayOutputStyle}>{currentInput}</div>
          </div>

          <div style={calculatorButtonsStyle}>
            <button style={numberBtnStyle} onClick={() => appendNumber('7')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>7</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('8')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>8</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('9')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>9</button>
            <button style={operatorBtnStyle} onClick={() => setOperatorHandler('+')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>+</button>
            <button style={functionBtnStyle} onClick={backspace} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnBack")}</button>

            <button style={numberBtnStyle} onClick={() => appendNumber('4')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>4</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('5')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>5</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('6')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>6</button>
            <button style={operatorBtnStyle} onClick={() => setOperatorHandler('-')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>−</button>
            <button style={memoryBtnStyle} onClick={useAnswer} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnAns")}</button>

            <button style={numberBtnStyle} onClick={() => appendNumber('1')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>1</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('2')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>2</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('3')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>3</button>
            <button style={operatorBtnStyle} onClick={() => setOperatorHandler('*')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>×</button>
            <button style={memoryBtnStyle} onClick={memoryAdd} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>M+</button>

            <button style={numberBtnStyle} onClick={() => appendNumber('0')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>0</button>
            <button style={numberBtnStyle} onClick={() => appendNumber('.')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>.</button>
            <button style={functionBtnStyle} onClick={addExponent} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnExp")}</button>
            <button style={operatorBtnStyle} onClick={() => setOperatorHandler('/')} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>÷</button>
            <button style={memoryBtnStyle} onClick={memorySubtract} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>M−</button>

            <button style={functionBtnStyle} onClick={changeSign} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnSign")}</button>
            <button style={functionBtnStyle} onClick={random} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnRnd")}</button>
            <button style={clearBtnStyle} onClick={clear} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnClear")}</button>
            <button style={equalsBtnStyle} onClick={calculate} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>=</button>
            <button style={memoryBtnStyle} onClick={memoryRecall} onMouseEnter={(e) => e.currentTarget.style.transform = 'translateY(-2px)'} onMouseLeave={(e) => e.currentTarget.style.transform = 'translateY(0)'}>{t("btnMr")}</button>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("introTitle")}</h2>
        <p style={paragraphStyle}>{t("introPara1")}</p>
        <p style={paragraphStyle}>{t("introPara2")}</p>
        <p style={paragraphStyle}>{t("introPara3")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("howToUseTitle")}</h2>
        <ol style={listStyle}>
          <li style={listItemStyle}>{t("howToStep1")}</li>
          <li style={listItemStyle}>{t("howToStep2")}</li>
          <li style={listItemStyle}>{t("howToStep3")}</li>
          <li style={listItemStyle}>{t("howToStep4")}</li>
          <li style={listItemStyle}>{t("howToStep5")}</li>
        </ol>

        <h3 style={h3Style}>{t("keyboardShortcutsTitle")}</h3>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("shortcut1")}</li>
          <li style={listItemStyle}>{t("shortcut2")}</li>
          <li style={listItemStyle}>{t("shortcut3")}</li>
          <li style={listItemStyle}>{t("shortcut4")}</li>
          <li style={listItemStyle}>{t("shortcut5")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("featuresTitle")}</h2>
        <div style={featureGridStyle}>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("feature1Title")}</div>
            <p style={featureTextStyle}>{t("feature1Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("feature2Title")}</div>
            <p style={featureTextStyle}>{t("feature2Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("feature3Title")}</div>
            <p style={featureTextStyle}>{t("feature3Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("feature4Title")}</div>
            <p style={featureTextStyle}>{t("feature4Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("feature5Title")}</div>
            <p style={featureTextStyle}>{t("feature5Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("feature6Title")}</div>
            <p style={featureTextStyle}>{t("feature6Text")}</p>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("insightsTitle")}</h2>
        <p style={paragraphStyle}>{t("insightsPara1")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle1")}</h3>
        <p style={paragraphStyle}>{t("insights1Para1")}</p>
        <p style={paragraphStyle}>{t("insights1Para2")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle2")}</h3>
        <p style={paragraphStyle}>{t("insights2Para1")}</p>
        <p style={paragraphStyle}>{t("insights2Para2")}</p>

        <h3 style={h3Style}>{t("insightsSubtitle3")}</h3>
        <p style={paragraphStyle}>{t("insights3Para1")}</p>
        <ul style={listStyle}>
          <li style={listItemStyle}>{t("insights3Point1")}</li>
          <li style={listItemStyle}>{t("insights3Point2")}</li>
          <li style={listItemStyle}>{t("insights3Point3")}</li>
          <li style={listItemStyle}>{t("insights3Point4")}</li>
        </ul>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("bestPracticesTitle")}</h2>

        <h3 style={h3Style}>{t("practice1Title")}</h3>
        <p style={paragraphStyle}>{t("practice1Text")}</p>

        <h3 style={h3Style}>{t("practice2Title")}</h3>
        <p style={paragraphStyle}>{t("practice2Text")}</p>

        <h3 style={h3Style}>{t("practice3Title")}</h3>
        <p style={paragraphStyle}>{t("practice3Text")}</p>

        <h3 style={h3Style}>{t("practice4Title")}</h3>
        <p style={paragraphStyle}>{t("practice4Text")}</p>

        <h3 style={h3Style}>{t("practice5Title")}</h3>
        <p style={paragraphStyle}>{t("practice5Text")}</p>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("useCasesTitle")}</h2>
        <div style={featureGridStyle}>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("useCase1Title")}</div>
            <p style={featureTextStyle}>{t("useCase1Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("useCase2Title")}</div>
            <p style={featureTextStyle}>{t("useCase2Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("useCase3Title")}</div>
            <p style={featureTextStyle}>{t("useCase3Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("useCase4Title")}</div>
            <p style={featureTextStyle}>{t("useCase4Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("useCase5Title")}</div>
            <p style={featureTextStyle}>{t("useCase5Text")}</p>
          </div>
          <div style={featureCardStyle}>
            <div style={featureTitleStyle}>{t("useCase6Title")}</div>
            <p style={featureTextStyle}>{t("useCase6Text")}</p>
          </div>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("faqTitle")}</h2>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq1Question")}</div>
          <p style={faqAnswerStyle}>{t("faq1Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq2Question")}</div>
          <p style={faqAnswerStyle}>{t("faq2Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq3Question")}</div>
          <p style={faqAnswerStyle}>{t("faq3Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq4Question")}</div>
          <p style={faqAnswerStyle}>{t("faq4Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq5Question")}</div>
          <p style={faqAnswerStyle}>{t("faq5Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq6Question")}</div>
          <p style={faqAnswerStyle}>{t("faq6Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq7Question")}</div>
          <p style={faqAnswerStyle}>{t("faq7Answer")}</p>
        </div>

        <div style={faqItemStyle}>
          <div style={faqQuestionStyle}>{t("faq8Question")}</div>
          <p style={faqAnswerStyle}>{t("faq8Answer")}</p>
        </div>
      </section>

      <section style={glassCardStyle}>
        <h2 style={h2Style}>{t("referencesTitle")}</h2>
        <ul style={referenceListStyle}>
          <li style={referenceItemStyle}>
            <a
              href="https://www.acko.com/calculators/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference1")}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://redstartec.com/blogs/news/scientific-or-basic-calculator"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference2")}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://www.calconic.com/blog/interactive-content-trends-2025"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference3")}
            </a>
          </li>
          <li style={referenceItemStyle}>
            <a
              href="https://guruathome.org/blog/when-should-students-use-calculators-us-guide/"
              target="_blank"
              rel="noopener noreferrer"
              style={referenceLinkStyle}
              onMouseEnter={(e) => e.currentTarget.style.color = colors.primaryHover}
              onMouseLeave={(e) => e.currentTarget.style.color = colors.primary}
            >
              {t("reference4")}
            </a>
          </li>
        </ul>
      </section>
    </div>
  );
}